import React, { useEffect, useState } from 'react';
import { QueryClient, QueryClientProvider } from 'react-query';
import { BrowserRouter as Router, Switch, Route, Redirect } from 'react-router-dom';
import utilStorage from '@shared/services/util-storage';
import { useCurrentUserActions } from '@shared/context/current-user';
import Dashboard from '@shared/layouts/Dashboard';
import RouterWrapper from '@shared/components/RouterWrapper';
import KeycloakComponentWrapper from '@src/shared/components/KeycloakComponentWrapper';
import useStorageLayer from '@shared/hooks/use-storage-layer';
import SplashScreen from '@src/shared/components/SplashScreen';
import { ReactKeycloakProvider } from '@react-keycloak/web';
import keycloak from '@src/keycloak';
import { useKeycloakUserActions, useKeycloakUserState } from '@src/shared/context/keycloak-user';
import authEvents from '@src/shared/services/auth-events';
import api from '@shared/services/restaurant-api';
import DatadogService from '@shared/services/datadog';
import useProgressiveWebApp from '@src/shared/hooks/use-pwa';
import { trackPWAIsActivated } from '@src/shared/tracking/pwa';
import { PAGE_TYPE } from '@src/pages/NewOverview';
import useFMFeatureFlag from '@src/shared/hooks/use-fm-featureflag';
import {
  JETFM_MCP_OCF_LANDING_PAGE_REDIRECTION,
  JETFM_NEW_OVERVIEW_PAGE_SWITCH,
} from '@src/shared/models';
import FirebaseConnector from '../FirebaseConnector';
import useOCFStatus from '@src/shared/hooks/use-ocf-status';
import { useOnboardingStore } from '@src/shared/store/onboarding/onboarding-store';
import usePartnerhubIntegrationDetection from '@src/shared/hooks/use-partnerhub-integration-detection';
import EventInitializerDashboard from '@src/pages/DeliveryDashboard/components/EventInitalizerDashboard';
import useSSEErrorHandler from '@src/pages/Deliveries/hooks/use-sse-error-handler';
import { useDashboardUIStore } from '@src/pages/DeliveryDashboard/store/dashboardUIStore';
import { DeliveryDashboardScreens } from '@src/pages/DeliveryDashboard/models/model-enum';

const globalQueryClient = new QueryClient();

export interface KeycloakTokens {
  idToken?: string;
  refreshToken?: string;
  token?: string;
}

const ContentDomain = () => {
  const environment = process.env.NODE_ENV || 'development';
  const isNewOverviewPageActive = useFMFeatureFlag(JETFM_NEW_OVERVIEW_PAGE_SWITCH);
  const { initialAuthCheckDone, isProductOverviewSeen } = useStorageLayer();
  const { setIsProductOverviewSeen } = useCurrentUserActions();
  const { setOnboardingActive, setReminderActive, setIsPartnerhubSession } = useOnboardingStore();
  const { setKeycloakToken, setKeycloakRestaurantData, updateLoadingState } =
    useKeycloakUserActions();
  const [onTokens, setOnTokens] = useState<KeycloakTokens>();
  const { isUserSwitchedBetweenApps, isAppInstalled } = useProgressiveWebApp();
  const { restaurant, keycloakToken } = useKeycloakUserState();
  const Legals = React.lazy(() => import('@src/pages/Legals'));
  const [keycloakDataRetrieved, setKeycloakDataRetrieved] = useState(false);
  const ErrorPage = React.lazy(() => import('@src/pages/Error'));
  const [activatePage, setActivatePage] = useState(false);
  const { isOCFEnabled } = useOCFStatus();
  const { SSEerror } = useSSEErrorHandler();
  const { setActiveScreen } = useDashboardUIStore();
  const { isPartnerhubRedirection, isPartnerhubWithActivateStats } =
    usePartnerhubIntegrationDetection();
  const CourierAppSupportPage = React.lazy(() => import('@src/pages/CourierAppSupport'));

  const ProductOverviewPage = React.lazy(() => import('@src/pages/ProductOverview'));

  const CourierOverviewPage = React.lazy(() => import('@src/pages/CourierOverview'));

  const NewOverviewPage = React.lazy(() => import('@src/pages/NewOverview'));

  //JET-FM OCF FLAGS
  const MCPOCFLandingPageRedirection = useFMFeatureFlag(JETFM_MCP_OCF_LANDING_PAGE_REDIRECTION);

  const eventLogger = (event: any, error: any) => {
    if (error) {
      console.log('Event On Error', {
        error: error.error,
        description: error.error_description,
      });
    }
    switch (event) {
      case 'onAuthError':
        authEvents.emitLogoutKeycloakUser(true);
        break;
      case 'onAuthRefreshError':
        authEvents.emitLogoutKeycloakUser(true);
        break;
      default:
    }
  };

  const fetchRestaurantData = async (token: string) => {
    try {
      const restaurantData = await api.getRestaurantData(token);
      if (restaurantData[0]) {
        DatadogService.setUser({
          id: restaurantData[0].id,
          name: restaurantData[0].name,
        });
      }
      return setKeycloakRestaurantData(restaurantData[0]);
    } catch (error) {
      DatadogService.createError(error, { type: 'Restaurant API Fetch Error' });
      updateLoadingState(true);
    }
  };

  useEffect(() => {
    setIsProductOverviewSeen(utilStorage.isProductOverviewAlreadySeen());
    if (isPartnerhubRedirection) {
      setIsPartnerhubSession(true);
      setOnboardingActive(false);
      setReminderActive(false);
    }
  }, []);

  useEffect(() => {
    if (onTokens && onTokens?.token) {
      (async () => {
        setKeycloakToken(onTokens?.token as string);
        await fetchRestaurantData(onTokens?.token as string);
        authEvents.emitKeycloakToken(onTokens?.token);
        setKeycloakDataRetrieved(true);
      })();
    } else if (onTokens && onTokens?.token === undefined) {
      setKeycloakDataRetrieved(true);
    }
  }, [onTokens]);

  useEffect(() => {
    if (isUserSwitchedBetweenApps && isAppInstalled) {
      trackPWAIsActivated(restaurant?.country || '');
    }
  }, [isUserSwitchedBetweenApps]);

  const getCompatibleInitialPage = () => {
    if (isPartnerhubRedirection) {
      return;
    }
    const SESSION_STORAGE_INITIAL_PAGE_REDIRECTION_KEY = 'initialPageRedirectionDone';
    const initialPageRedirectionDone = sessionStorage.getItem(
      SESSION_STORAGE_INITIAL_PAGE_REDIRECTION_KEY
    );
    if (MCPOCFLandingPageRedirection && isOCFEnabled !== null) {
      if (initialPageRedirectionDone === 'true') {
        return;
      }
      if (location.pathname.includes('/couriers')) {
        //console.log('OCF-ROUTECHECK ---> Already on Couriers Page');
        return;
      }
      window.history.pushState({}, '', '/couriers');
      sessionStorage.setItem(SESSION_STORAGE_INITIAL_PAGE_REDIRECTION_KEY, 'true');
    } else {
      if (location.pathname.includes('/deliveries')) {
        //console.log('OCF-ROUTECHECK ---> Already on Deliveries Page');
        return;
      }
      //console.log('OCF-ROUTECHECK ---> Redirecting to Deliveries Page');
      window.history.pushState({}, '', '/deliveries');
    }
  };

  useEffect(() => {
    if (keycloakDataRetrieved && !keycloakToken) {
      setActivatePage(true);
    } else if (
      keycloakDataRetrieved &&
      keycloakToken &&
      restaurant?.country &&
      isOCFEnabled !== null &&
      MCPOCFLandingPageRedirection
    ) {
      getCompatibleInitialPage();
      setActivatePage(true);
    }
  }, [MCPOCFLandingPageRedirection, isOCFEnabled, restaurant, keycloakDataRetrieved, keycloak]);

  useEffect(() => {
    if (!activatePage) return;
    if (isPartnerhubWithActivateStats) {
      setActiveScreen(DeliveryDashboardScreens.DELIVERY_DATA_SCREEN);
    }
  }, [activatePage]);

  if (!activatePage) {
    return (
      <ReactKeycloakProvider
        authClient={keycloak}
        onEvent={eventLogger}
        onTokens={setOnTokens}
        initOptions={{
          pkceMethod: 'S256',
          checkLoginIframe: false,
          responseMode: 'query',
          enableLogging: true,
          onLoad: 'check-sso',
        }}
      >
        <SplashScreen />
      </ReactKeycloakProvider>
    );
  }

  return (
    <ReactKeycloakProvider
      authClient={keycloak}
      onEvent={eventLogger}
      onTokens={setOnTokens}
      initOptions={{
        pkceMethod: 'S256',
        checkLoginIframe: false,
        responseMode: 'query',
        enableLogging: true,
        onLoad: 'check-sso',
      }}
      LoadingComponent={<SplashScreen />}
    >
      <QueryClientProvider client={globalQueryClient}>
        <FirebaseConnector />
        {initialAuthCheckDone ? (
          <Router>
            <RouterWrapper>
              {isProductOverviewSeen ? (
                <Switch>
                  <Route exact path="/">
                    <Redirect to={'/deliveries'} />
                  </Route>
                  <Route
                    exact
                    path={['/couriers', '/deliveries', '/not-supported', '/delivery-history']}
                  >
                    <>
                      <KeycloakComponentWrapper>
                        <EventInitializerDashboard SSEerror={SSEerror} />
                        <Dashboard />
                      </KeycloakComponentWrapper>
                    </>
                  </Route>
                  <Route
                    exact
                    path={[
                      '/impressum',
                      '/privacy',
                      '/terms-of-use',
                      '/cookie-statement',
                      '/technologies',
                    ]}
                  >
                    <Legals />
                  </Route>
                  <Route exact path="/courier-app-order-tutorial">
                    <CourierAppSupportPage />
                  </Route>
                  <Route exact path="/courier-app-bag-tutorial">
                    <CourierAppSupportPage />
                  </Route>
                  <Route exact path="/product-overview">
                    <KeycloakComponentWrapper allowAnonymousAccess={true}>
                      {isNewOverviewPageActive === true && (
                        <NewOverviewPage predefinedPageType={PAGE_TYPE.MANAGER} />
                      )}
                      {keycloakDataRetrieved && isNewOverviewPageActive === false && (
                        <ProductOverviewPage />
                      )}
                    </KeycloakComponentWrapper>
                  </Route>
                  <Route exact path="/courier-overview">
                    <KeycloakComponentWrapper allowAnonymousAccess={true}>
                      {keycloakDataRetrieved && isNewOverviewPageActive === true && (
                        <NewOverviewPage predefinedPageType={PAGE_TYPE.COURIER} />
                      )}
                      {keycloakDataRetrieved && isNewOverviewPageActive === false && (
                        <CourierOverviewPage />
                      )}
                    </KeycloakComponentWrapper>
                  </Route>
                  {environment !== 'production' && (
                    <Route exact path="/new-overview">
                      <KeycloakComponentWrapper allowAnonymousAccess={true}>
                        {keycloakDataRetrieved && isNewOverviewPageActive === true && (
                          <NewOverviewPage predefinedPageType={PAGE_TYPE.MANAGER} />
                        )}
                        {keycloakDataRetrieved && isNewOverviewPageActive === false && (
                          <ProductOverviewPage />
                        )}
                      </KeycloakComponentWrapper>
                    </Route>
                  )}
                  <Route path="*">
                    <>
                      <KeycloakComponentWrapper>
                        <ErrorPage />
                      </KeycloakComponentWrapper>
                    </>
                  </Route>
                </Switch>
              ) : (
                <Switch>
                  <Route exact path="/courier-app-order-tutorial">
                    <CourierAppSupportPage />
                  </Route>
                  <Route exact path="/courier-app-bag-tutorial">
                    <CourierAppSupportPage />
                  </Route>
                  <Route exact path="/product-overview">
                    <KeycloakComponentWrapper allowAnonymousAccess={true}>
                      {keycloakDataRetrieved && isNewOverviewPageActive === true && (
                        <NewOverviewPage predefinedPageType={PAGE_TYPE.MANAGER} />
                      )}
                      {keycloakDataRetrieved && isNewOverviewPageActive === false && (
                        <ProductOverviewPage />
                      )}
                    </KeycloakComponentWrapper>
                  </Route>
                  <Route exact path="/courier-app-order-tutorial">
                    <CourierAppSupportPage />
                  </Route>
                  <Route exact path="/courier-app-bag-tutorial">
                    <CourierAppSupportPage />
                  </Route>
                  <Route exact path="/courier-overview">
                    <KeycloakComponentWrapper allowAnonymousAccess={true}>
                      {keycloakDataRetrieved && isNewOverviewPageActive === true && (
                        <NewOverviewPage predefinedPageType={PAGE_TYPE.COURIER} />
                      )}
                      {keycloakDataRetrieved && isNewOverviewPageActive === false && (
                        <CourierOverviewPage />
                      )}
                    </KeycloakComponentWrapper>
                  </Route>
                  {environment !== 'production' && (
                    <Route exact path="/new-overview">
                      <KeycloakComponentWrapper allowAnonymousAccess={true}>
                        {keycloakDataRetrieved && isNewOverviewPageActive === true && (
                          <NewOverviewPage predefinedPageType={PAGE_TYPE.COURIER} />
                        )}
                        {keycloakDataRetrieved && isNewOverviewPageActive === false && (
                          <CourierOverviewPage />
                        )}
                      </KeycloakComponentWrapper>
                    </Route>
                  )}
                  <Route
                    exact
                    path={[
                      '/impressum',
                      '/privacy',
                      '/terms-of-use',
                      '/cookie-statement',
                      '/technologies',
                    ]}
                  >
                    <Legals />
                  </Route>
                  <Route path="*">
                    <Redirect to="/product-overview" />
                  </Route>
                </Switch>
              )}
            </RouterWrapper>
          </Router>
        ) : (
          <SplashScreen />
        )}
      </QueryClientProvider>
    </ReactKeycloakProvider>
  );
};

export default ContentDomain;
